import { useCallback, useMemo } from 'react';

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

const GAZ_DELIVERY_CATEGORY_NAMES = new Set([
  'бордюр',
  'бордюр дорожный',
  'брусчатка',
  'брусчатка и тротуарная плитка',
  'плитка тротуарная',
  'газонная решетка',
  'лоток водоотводный жби',
  'лоток водоотводный тротуарный',
]);

const normalizeRegionCode = (value) => {
  const rawRegion = String(value || '').trim().toUpperCase();
  if (rawRegion === 'МОСКВА' || rawRegion === 'МОСКОВСКАЯ ОБЛАСТЬ') return 'МСК';
  if (rawRegion === 'САНКТ-ПЕТЕРБУРГ' || rawRegion === 'ЛЕНИНГРАДСКАЯ ОБЛАСТЬ') return 'СПБ';
  return rawRegion;
};

const shouldUseGazDeliveryCategoryByRegion = (region) => (
  ['МСК', 'КРД'].includes(normalizeRegionCode(region))
);

const isGazDeliveryCategory = (value, region) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized.includes('газобетон')) {
    return true;
  }

  if (!GAZ_DELIVERY_CATEGORY_NAMES.has(normalized)) {
    return false;
  }

  return shouldUseGazDeliveryCategoryByRegion(region);
};

// useDelivery предоставляет функцию для перерасчета доставки
export const useDelivery = (addressAndCoordinates) => {
  // Мемоизируем addressAndCoordinates, чтобы избежать пересоздания
  const memoizedAddressAndCoordinates = useMemo(() => {
    // Поддерживаем оба формата координат
    const coords = addressAndCoordinates?.coordinates || addressAndCoordinates;
    const lat = coords?.lat ?? coords?.latitude ?? 0;
    const lng = coords?.lng ?? coords?.longitude ?? 0;

    return {
      address: addressAndCoordinates?.address || '',
      region: addressAndCoordinates?.region || null,
      coordinates: {
        lat,
        lng,
        latitude: lat,
        longitude: lng
      },
    };
  }, [addressAndCoordinates]);

  // Функция для перерасчета доставки
  const recalculateDelivery = useCallback(async (materials, deliveryMethod, selectedRegion = null) => {
    const rawDeliveryMethod = typeof deliveryMethod === 'object'
      ? deliveryMethod?.name
      : deliveryMethod;
    const explicitUnloadMethod = typeof deliveryMethod === 'object'
      ? deliveryMethod?.unloadMethod
      : null;
    const explicitMskDeliveryMode = typeof deliveryMethod === 'object'
      ? deliveryMethod?.mskDeliveryMode
      : null;

    if (!materials || materials.length === 0 || !rawDeliveryMethod || rawDeliveryMethod === 'Самовывоз') {
      console.log('No valid input for delivery calculation:', { materials, deliveryMethod });
      return null; // Возвращаем null для недоступной доставки
    }

    try {
      const isGazMaterial = (material) => {
        const materialRegion =
          material?.region ||
          selectedRegion ||
          addressAndCoordinates?.region ||
          memoizedAddressAndCoordinates?.region;
        const calculator = String(material?.calculator || material?.calculatorCategory || '').toLowerCase();
        const category = String(material?.category || '').toLowerCase();
        return isGazDeliveryCategory(calculator, materialRegion) || isGazDeliveryCategory(category, materialRegion);
      };

      const isGazRecalculation = Array.isArray(materials) && materials.length > 0 && materials.every(isGazMaterial);

      const normalizeGazDeliveryMethod = (value) => {
        const method = String(value || '');
        if (method.includes('Манипулятор с прицепом')) return 'Манипулятор с прицепом';
        if (method.includes('Манипулятор 10тн')) return 'Манипулятор 10тн';
        if (method.includes('Шаланда')) return 'Шаланда';
        return method;
      };

      const resolveGazUnloadMethod = (value) => {
        const method = String(value || '').toLowerCase();
        if (method.includes('без разгрузки')) return 'без разгрузки';
        if (method.includes('кран')) return 'разгрузка краном';
        return 'разгрузка манипулятором';
      };

      const manufacturer = String(materials?.[0]?.manufacturer || '').trim();
      const endpoint = isGazRecalculation ? '/api/delivery_gaz' : '/api/delivery_os';
      const requestData = {
        materials,
        deliveryMethod: isGazRecalculation ? normalizeGazDeliveryMethod(rawDeliveryMethod) : rawDeliveryMethod,
        addressAndCoordinates: {
          ...memoizedAddressAndCoordinates,
          region: selectedRegion || addressAndCoordinates?.region || memoizedAddressAndCoordinates?.region || null,
        },
        ...(isGazRecalculation
          ? {
            unloadMethod: explicitUnloadMethod || resolveGazUnloadMethod(rawDeliveryMethod),
            productionInfo: { NAME: manufacturer },
          }
          : {
            mskDeliveryMode: explicitMskDeliveryMode || "factory_to_client",
          }),
      };

      console.log('Данные для расчёта доставки:', requestData);
      console.log('Координаты:', memoizedAddressAndCoordinates.coordinates);
      console.log('Материалы для доставки:', materials);

      const response = await fetch(`${PATH_TO_BACKEND}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      const result = await response.json();
      console.log('Ответ от API доставки:', result);

      if (!response.ok || result.status !== 'ok' || !result.data) {
        console.warn('Failed to get valid delivery data:', { status: response.status, message: result.message });
        return null; // Возвращаем null при ошибке
      }

      const {
        numberOfTrips,
        costPerTrip,
        bestMethod,
        bitrixId,
        deliveryBreakdown,
        calculationMode,
        usedCentralized,
      } = result.data;
      if (numberOfTrips === undefined || numberOfTrips === null || costPerTrip === undefined || costPerTrip === null) {
        console.warn('Missing required delivery data:', result.data);
        return null; // Возвращаем null, если данные некорректны
      }

      const totalCost = numberOfTrips * costPerTrip;

      const deliveryData = {
        bitrixId: bitrixId,
        name: bestMethod || rawDeliveryMethod, // Используем bestMethod из ответа сервера, если он есть
        numberOfTrips: numberOfTrips || 0,
        costPerTrip: costPerTrip || 0,
        totalCost: totalCost || 0,
        ...(isGazRecalculation
          ? {
            deliveryBreakdown: deliveryBreakdown || null,
            calculationMode: calculationMode || null,
            usedCentralized: Boolean(usedCentralized),
          }
          : {}),
      };

      console.log('Расчитанная доставка:', deliveryData);
      return deliveryData;
    } catch (error) {
      console.error('Ошибка при расчете доставки:', error);
      return null; // Возвращаем null при ошибке
    }
  }, [memoizedAddressAndCoordinates]);

  return { recalculateDelivery };
};
